/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from 'react';

import useItems from '../../hooks/useItems';
import { useIntersectionObserver } from '../../hooks/useObserver';
import * as AppPropTypes from '../../lib/PropTypes';

import { useApi } from '../../contexts/ApiContext';
import CardsList from './CardsList';

import styles from '../../../styles/lists/articles-list.module.scss';

const propTypes = {
    title: AppPropTypes.text,
    description: AppPropTypes.text,
    link: AppPropTypes.link,
    items: PropTypes.arrayOf(PropTypes.oneOfType([AppPropTypes.event])),
    query: PropTypes.shape({
        count: PropTypes.string,
    }),
    cardType: PropTypes.string,
    cardProps: PropTypes.shape({
        isStacked: PropTypes.bool,
    }),
    lazyLoad: PropTypes.bool,
    withoutColumns: PropTypes.bool,
    className: PropTypes.string,
    listClassName: PropTypes.string,
    cardClassName: PropTypes.string,
};

const defaultProps = {
    title: null,
    description: null,
    link: null,
    items: null,
    query: null,
    cardType: 'article',
    cardProps: null,
    lazyLoad: false,
    withoutColumns: false,
    className: null,
    listClassName: null,
    cardClassName: null,
};

const ArticlesList = ({
    title,
    description,
    link,
    items: initialItems,
    query,
    cardType,
    cardProps,
    lazyLoad,
    withoutColumns,
    className,
    listClassName,
    cardClassName,
    ...props
}) => {
    const api = useApi();

    const {
        ref,
        entry: { isIntersecting = false },
    } = useIntersectionObserver({ rootMargin: '-10%' });

    const [wasVisible, setWasVisible] = useState(isIntersecting);
    const shouldLoad = !lazyLoad || wasVisible;
    const getItems = useMemo(
        () =>
            shouldLoad
                ? () => {
                      const { related = null, count = null, ...otherQuery } = query || {};
                      return related !== null
                          ? api.articles.getRelated(related, otherQuery, 12)
                          : api.articles
                                .get({
                                    ...query,
                                    paginated: false,
                                    count: count || 5,
                                })
                                .then((response) => {
                                    const { pages = [] } = response || {};
                                    const [currentPage = null] = pages || [];
                                    return currentPage || null;
                                });
                  }
                : null,
        [api, query, shouldLoad],
    );

    const { items } = useItems(getItems, {
        items: initialItems,
    });

    useEffect(() => {
        if (lazyLoad && isIntersecting && !wasVisible) {
            setWasVisible(true);
        }
    }, [lazyLoad, isIntersecting, wasVisible, setWasVisible]);

    return (
        <CardsList
            ref={ref}
            withRef
            withPlaceholderIndex
            className={classNames([styles.container, { [className]: className !== null }])}
            listClassName={
                !withoutColumns
                    ? classNames([styles.list, { [listClassName]: listClassName !== null }])
                    : null
            }
            cardClassName={
                !withoutColumns
                    ? classNames([styles.card, { [cardClassName]: cardClassName !== null }])
                    : null
            }
            title={title}
            description={description}
            link={link}
            items={items}
            cardType={cardType}
            cardProps={cardProps}
            {...props}
        />
    );
};

ArticlesList.propTypes = propTypes;
ArticlesList.defaultProps = defaultProps;

export default ArticlesList;
